import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  APIProvider, Map, AdvancedMarker, InfoWindow, useMap,
} from "@vis.gl/react-google-maps";
import Supercluster from "supercluster";
import {
  FaMapMarkerAlt, FaEnvelopeOpen, FaMousePointer, FaPaperPlane,
  FaChartLine, FaFileAlt, FaEye, FaHeart, FaShareAlt, FaPencilAlt,
  FaBed, FaBath, FaCar, FaRulerCombined, FaBuilding, FaCog, FaBell,
  FaBan, FaExclamationTriangle, FaCalendarAlt, FaChevronDown,
  FaFilter, FaSearch, FaTimes, FaSms, FaCommentSlash,
} from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { handleTokenAndPostRequest } from "../utils/utils";
import { useViewScope } from "../contexts/ViewScopeContext";
import DEMO_MAP_ADDRESSES from "../demo/demoMapProspector";
import DemoBanner from "../demo/DemoBanner";
import useDemoNetworkGuard from "../demo/useDemoNetworkGuard";

const GOOGLE_MAPS_API_KEY = "AIzaSyCtn44ogwI_q0TGiuwC1X_sOXYijuEdr5M";
const MAP_ID = "1ae451cd191c1a06";

// ── Event display config ────────────────────────────────────
const EVENT_CONFIG = {
  // Platform events
  property_analysed:          { label: "Property Analysed",      Icon: FaChartLine,      color: "#7c3aed" },
  analysis_started:           { label: "Analysis Started",       Icon: FaChartLine,      color: "#be185d" },
  click_analyse_property:     { label: "Analyse Property",       Icon: FaChartLine,      color: "#4338ca" },
  send_property_report:       { label: "Report Sent",            Icon: FaFileAlt,        color: "#6d28d9" },
  view_appraisal:             { label: "Viewed Appraisal",       Icon: FaEye,            color: "#be123c" },
  click_view_appraisal:       { label: "View Appraisal",         Icon: FaEye,            color: "#be123c" },
  click_track_property:       { label: "Track Property",         Icon: FaHeart,          color: "#be185d" },
  click_share_valuation:      { label: "Share Valuation",        Icon: FaShareAlt,       color: "#6d28d9" },
  click_edit_property_details:{ label: "Edit Property",          Icon: FaPencilAlt,      color: "#92400e" },
  change_bedrooms:            { label: "Changed Bedrooms",       Icon: FaBed,            color: "#c2410c" },
  change_bathrooms:           { label: "Changed Bathrooms",      Icon: FaBath,           color: "#c2410c" },
  change_carspaces:           { label: "Changed Car Spaces",     Icon: FaCar,            color: "#c2410c" },
  change_land_area:           { label: "Changed Land Area",      Icon: FaRulerCombined,  color: "#c2410c" },
  change_property_type:       { label: "Changed Type",           Icon: FaBuilding,       color: "#92400e" },
  save_notification_settings: { label: "Saved Settings",         Icon: FaCog,            color: "#047857" },
  // Email events
  Open:                       { label: "Opened Email",           Icon: FaEnvelopeOpen,   color: "#047857" },
  Click:                      { label: "Clicked Email",          Icon: FaMousePointer,   color: "#4338ca" },
  Delivery:                   { label: "Email Delivered",        Icon: FaPaperPlane,     color: "#1d4ed8" },
  Bounce:                     { label: "Email Bounced",          Icon: FaExclamationTriangle, color: "#b91c1c" },
  SpamComplaint:              { label: "Spam Complaint",         Icon: FaBan,            color: "#991b1b" },
  SubscriptionChange:         { label: "Unsubscribed",           Icon: FaBell,           color: "#92400e" },
  // SMS events
  sms_sent:                   { label: "SMS Sent",               Icon: FaSms,            color: "#166534" },
  sms_unsubscribe:            { label: "SMS Unsubscribed",       Icon: FaCommentSlash,   color: "#b91c1c" },
};

const CAMPAIGN_LABELS = {
  market_update: "Market Update",
  property_update: "Property Update",
  onboarding_welcome: "Welcome",
  property_report: "Property Report",
  agent_property_note: "Agent Note",
  share_property: "Share Property",
  appraisal_send: "Appraisal",
};

const CAMPAIGN_OPTIONS = [
  { value: "", label: "All campaigns" },
  { value: "market_update", label: "Market Update" },
  { value: "property_update", label: "Property Update" },
  { value: "onboarding_welcome", label: "Onboarding Welcome" },
  { value: "property_report", label: "Property Report" },
  { value: "agent_property_note", label: "Agent Note" },
  { value: "share_property", label: "Share Property" },
  { value: "appraisal_send", label: "Appraisal" },
];

const EMAIL_EVENT_OPTIONS = [
  { value: "Open", label: "Opened" },
  { value: "Click", label: "Clicked" },
];

const PLATFORM_EVENT_OPTIONS = [
  { value: "property_analysed", label: "Property Analysed" },
  { value: "analysis_started", label: "Analysis Started" },
  { value: "click_analyse_property", label: "Analyse Property" },
  { value: "view_appraisal", label: "Viewed Appraisal" },
  { value: "click_view_appraisal", label: "View Appraisal" },
  { value: "send_property_report", label: "Report Sent" },
  { value: "click_track_property", label: "Track Property" },
  { value: "click_share_valuation", label: "Share Valuation" },
  { value: "click_edit_property_details", label: "Edit Property" },
  { value: "change_bedrooms", label: "Changed Bedrooms" },
  { value: "change_bathrooms", label: "Changed Bathrooms" },
  { value: "change_carspaces", label: "Changed Car Spaces" },
  { value: "change_land_area", label: "Changed Land Area" },
  { value: "change_property_type", label: "Changed Type" },
  { value: "save_notification_settings", label: "Saved Settings" },
];

const SMS_EVENT_OPTIONS = [
  { value: "sms_sent", label: "SMS Sent" },
  { value: "sms_unsubscribe", label: "SMS Unsubscribed" },
];

const DATE_RANGE_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "28", label: "Last 28 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 6 months" },
  { value: "365", label: "Last year" },
  { value: "all", label: "All time" },
];

function formatTimestamp(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: true });
}

// ── Cluster component using useMap hook ─────────────────────
function ClusteredMarkers({ addresses, onMarkerClick, activeMarkerId }) {
  const map = useMap();
  const [clusters, setClusters] = useState([]);
  const [zoom, setZoom] = useState(12);
  const [bounds, setBounds] = useState(null);

  const supercluster = useMemo(() => {
    const sc = new Supercluster({
      radius: 60,
      maxZoom: 18,
      minZoom: 0,
    });
    const points = addresses.map((addr) => ({
      type: "Feature",
      properties: { ...addr },
      geometry: { type: "Point", coordinates: [addr.lng, addr.lat] },
    }));
    sc.load(points);
    return sc;
  }, [addresses]);

  const updateClusters = useCallback(() => {
    if (!map) return;
    const b = map.getBounds();
    if (!b) return;
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    const currentBounds = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];
    const currentZoom = Math.floor(map.getZoom());
    setBounds(currentBounds);
    setZoom(currentZoom);
    const cl = supercluster.getClusters(currentBounds, currentZoom);
    setClusters(cl);
  }, [map, supercluster]);

  // Center map on the densest cluster of addresses (median-based, ignoring outliers)
  useEffect(() => {
    if (!map || !addresses.length) return;
    const lats = addresses.map(a => a.lat).sort((a, b) => a - b);
    const lngs = addresses.map(a => a.lng).sort((a, b) => a - b);
    // Use middle 80% to exclude outliers
    const trim = Math.floor(addresses.length * 0.1);
    const coreLats = lats.slice(trim, lats.length - trim || lats.length);
    const coreLngs = lngs.slice(trim, lngs.length - trim || lngs.length);
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: coreLats[0], lng: coreLngs[0] });
    bounds.extend({ lat: coreLats[coreLats.length - 1], lng: coreLngs[coreLngs.length - 1] });
    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
  }, [map, addresses]);

  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("idle", updateClusters);
    updateClusters();
    return () => listener.remove();
  }, [map, updateClusters]);

  return (
    <>
      {clusters.map((cluster) => {
        const [lng, lat] = cluster.geometry.coordinates;
        const isCluster = cluster.properties.cluster;

        if (isCluster) {
          const count = cluster.properties.point_count;
          const size = Math.min(24 + Math.log2(count) * 8, 56);
          return (
            <AdvancedMarker
              key={`cluster-${cluster.id}`}
              position={{ lat, lng }}
              zIndex={10}
              onClick={() => {
                const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(cluster.id), 20);
                map.panTo({ lat, lng });
                map.setZoom(expansionZoom);
              }}
            >
              <div
                style={{
                  width: size, height: size, borderRadius: "50%",
                  background: "linear-gradient(135deg, #1a3f6b, #10b981)",
                  color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: size < 36 ? 12 : 14,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  cursor: "pointer", border: "2px solid #fff",
                }}
              >
                {count}
              </div>
            </AdvancedMarker>
          );
        }

        const addr = cluster.properties;
        const isActive = activeMarkerId === addr.property_id;
        const eventCount = addr.events?.length || 0;

        return (
          <AdvancedMarker
            key={`pin-${addr.property_id}`}
            position={{ lat, lng }}
            zIndex={isActive ? 100 : 1}
            onClick={() => onMarkerClick(addr.property_id)}
          >
            <div style={{ cursor: "pointer", position: "relative" }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: isActive ? "#1a3f6b" : "#10b981",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: isActive ? "0 0 0 4px rgba(26,63,107,0.3), 0 2px 8px rgba(0,0,0,0.3)" : "0 2px 6px rgba(0,0,0,0.25)",
                border: "2px solid #fff",
                transition: "all 0.15s",
              }}>
                <FaMapMarkerAlt size={14} color="#fff" />
              </div>
              {eventCount > 1 && (
                <div style={{
                  position: "absolute", top: -6, right: -6,
                  width: 18, height: 18, borderRadius: "50%",
                  background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1.5px solid #fff",
                }}>
                  {eventCount > 9 ? "9+" : eventCount}
                </div>
              )}
              <div style={{
                width: 0, height: 0,
                borderLeft: "6px solid transparent", borderRight: "6px solid transparent",
                borderTop: `6px solid ${isActive ? "#1a3f6b" : "#10b981"}`,
                margin: "-1px auto 0",
              }} />
            </div>
          </AdvancedMarker>
        );
      })}
    </>
  );
}

// ── Main component ──────────────────────────────────────────
export default function MapProspector() {
  const { viewScope, agentFilter } = useViewScope();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("90");
  const [campaign, setCampaign] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  const [emailSearch, setEmailSearch] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  const dateDropdownRef = useRef(null);
  const eventDropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const panelRef = useRef(null);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    function handleClick(e) {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) {
        setDateDropdownOpen(false);
      }
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(e.target)) {
        setEventDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/map-prospector`,
        {
          date_range: dateRange,
          view_scope: viewScope,
          agent_filter: agentFilter,
          campaign,
          event_types: eventTypes,
          email_search: contactEmail || emailSearch,
        }
      );
      const d = res.data || res;
      setAddresses(Array.isArray(d.addresses) ? d.addresses : []);
    } catch {
      setAddresses([]);
    }
    setLoading(false);
  }, [dateRange, viewScope, agentFilter, campaign, eventTypes, contactEmail, emailSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // When the agent has no address activity at all and isn't narrowing by a
  // filter or search, show a populated sample view (centred on the Manly /
  // Northern Beaches area) instead of a blank map. Demo interactions are
  // sandboxed by useDemoNetworkGuard; the demo clears the moment real data
  // arrives or any filter is applied.
  const hasActiveFilter = !!campaign || eventTypes.length > 0 || !!contactEmail || !!emailSearch;
  const showDemo = !loading && addresses.length === 0 && !hasActiveFilter;
  useDemoNetworkGuard(showDemo);
  const displayAddresses = showDemo ? DEMO_MAP_ADDRESSES : addresses;

  const activeAddress = useMemo(
    () => displayAddresses.find(a => a.property_id === activeMarkerId) || null,
    [displayAddresses, activeMarkerId]
  );

  const center = useMemo(() => {
    if (!displayAddresses.length) return { lat: -33.7971, lng: 151.2876 }; // Manly default
    const latSum = displayAddresses.reduce((s, a) => s + a.lat, 0);
    const lngSum = displayAddresses.reduce((s, a) => s + a.lng, 0);
    return { lat: latSum / displayAddresses.length, lng: lngSum / displayAddresses.length };
  }, [displayAddresses]);

  const handleMarkerClick = useCallback((propertyId) => {
    setActiveMarkerId(prev => prev === propertyId ? null : propertyId);
  }, []);

  // Scroll panel into view on mobile when a pin is selected
  useEffect(() => {
    if (isMobile && activeMarkerId && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [activeMarkerId, isMobile]);

  const dateLabel = DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label || "Last 90 days";

  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      height: "100%", overflow: "hidden",
    }}>
      {/* Top bar */}
      <div style={{
        padding: isMobile ? "72px 16px 10px" : "16px 20px 10px",
        background: "#fff", borderBottom: "1px solid #e5e7eb",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0, color: "#1f2937" }}>
              Map Prospector
            </h1>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 0" }}>
              {loading ? "Loading..." : `${displayAddresses.length} address${displayAddresses.length !== 1 ? "es" : ""} with activity`}
            </p>
          </div>
        </div>

        {/* Filters row */}
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>

          {/* Email search */}
          <div style={{ position: "relative", minWidth: isMobile ? 140 : 180, flex: isMobile ? "1 1 100%" : "0 0 auto" }}>
            <FaSearch style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: isMobile ? 10 : 12 }} />
            <input
              type="text"
              placeholder="Search email..."
              value={emailSearch}
              onChange={e => setEmailSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") setContactEmail(emailSearch); }}
              style={{
                width: "100%", padding: isMobile ? "7px 28px 7px 30px" : "7px 32px 7px 32px", borderRadius: 8,
                border: "1px solid #e5e7eb", fontSize: isMobile ? 12 : 13, boxSizing: "border-box",
                background: "#fff", outline: "none",
              }}
            />
            {(contactEmail || emailSearch) && (
              <FaTimes
                onClick={() => { setEmailSearch(""); setContactEmail(""); }}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 12, cursor: "pointer" }}
              />
            )}
          </div>

          {/* Campaign filter */}
          <div style={{ position: "relative", minWidth: isMobile ? 120 : 160 }}>
            <FaFilter style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: isMobile ? 10 : 12 }} />
            <select
              value={campaign}
              onChange={e => setCampaign(e.target.value)}
              style={{
                width: "100%", padding: isMobile ? "7px 10px 7px 28px" : "7px 12px 7px 32px", borderRadius: 8,
                border: "1px solid #e5e7eb", fontSize: isMobile ? 12 : 13, boxSizing: "border-box",
                background: "#fff", outline: "none", cursor: "pointer",
                appearance: "none", WebkitAppearance: "none",
              }}
            >
              {CAMPAIGN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <FaChevronDown style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 10, pointerEvents: "none" }} />
          </div>

          {/* Event type multi-select */}
          <div ref={eventDropdownRef} style={{ position: "relative", minWidth: isMobile ? 140 : 180 }}>
            <button
              onClick={() => setEventDropdownOpen(p => !p)}
              style={{
                display: "flex", alignItems: "center", gap: 6, width: "100%",
                padding: isMobile ? "7px 10px" : "7px 12px", borderRadius: 8, border: "1px solid #e5e7eb",
                fontSize: isMobile ? 12 : 13, background: "#fff", cursor: "pointer", color: "#374151",
                boxSizing: "border-box",
              }}
            >
              {eventTypes.length === 0
                ? "All events"
                : `${eventTypes.length} event${eventTypes.length > 1 ? "s" : ""} selected`}
              {eventTypes.length > 0 && (
                <FaTimes
                  onClick={e => { e.stopPropagation(); setEventTypes([]); }}
                  style={{ fontSize: 10, color: "#9ca3af", marginLeft: 2, cursor: "pointer" }}
                />
              )}
              <FaChevronDown style={{ marginLeft: "auto", color: "#9ca3af", fontSize: 10 }} />
            </button>
            {eventDropdownOpen && (
              <div style={{
                position: "absolute", top: "100%", left: 0, marginTop: 4, minWidth: 240,
                background: "#fff", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                border: "1px solid #e5e7eb", zIndex: 100, maxHeight: 360, overflowY: "auto",
              }}>
                <div style={{ padding: "8px 12px 4px", fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Email Events</div>
                {EMAIL_EVENT_OPTIONS.map(o => {
                  const checked = eventTypes.includes(o.value);
                  const cfg = EVENT_CONFIG[o.value];
                  return (
                    <label
                      key={o.value}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "7px 12px", cursor: "pointer", fontSize: 13,
                        background: checked ? "#f0fdf4" : "#fff",
                      }}
                      onMouseEnter={e => { if (!checked) e.currentTarget.style.background = "#f9fafb"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = checked ? "#f0fdf4" : "#fff"; }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setEventTypes(prev => checked ? prev.filter(v => v !== o.value) : [...prev, o.value])}
                        style={{ accentColor: "#047857" }}
                      />
                      {cfg && <cfg.Icon style={{ fontSize: 11, color: cfg.color }} />}
                      <span style={{ color: checked ? "#047857" : "#374151", fontWeight: checked ? 600 : 400 }}>{o.label}</span>
                    </label>
                  );
                })}
                <div style={{ padding: "10px 12px 4px", fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", borderTop: "1px solid #f3f4f6" }}>Platform Events</div>
                {PLATFORM_EVENT_OPTIONS.map(o => {
                  const checked = eventTypes.includes(o.value);
                  const cfg = EVENT_CONFIG[o.value];
                  return (
                    <label
                      key={o.value}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "7px 12px", cursor: "pointer", fontSize: 13,
                        background: checked ? "#f0fdf4" : "#fff",
                      }}
                      onMouseEnter={e => { if (!checked) e.currentTarget.style.background = "#f9fafb"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = checked ? "#f0fdf4" : "#fff"; }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setEventTypes(prev => checked ? prev.filter(v => v !== o.value) : [...prev, o.value])}
                        style={{ accentColor: "#7c3aed" }}
                      />
                      {cfg && <cfg.Icon style={{ fontSize: 11, color: cfg.color }} />}
                      <span style={{ color: checked ? "#047857" : "#374151", fontWeight: checked ? 600 : 400 }}>{o.label}</span>
                    </label>
                  );
                })}
                <div style={{ padding: "10px 12px 4px", fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", borderTop: "1px solid #f3f4f6" }}>SMS Events</div>
                {SMS_EVENT_OPTIONS.map(o => {
                  const checked = eventTypes.includes(o.value);
                  const cfg = EVENT_CONFIG[o.value];
                  return (
                    <label
                      key={o.value}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "7px 12px", cursor: "pointer", fontSize: 13,
                        background: checked ? "#f0fdf4" : "#fff",
                      }}
                      onMouseEnter={e => { if (!checked) e.currentTarget.style.background = "#f9fafb"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = checked ? "#f0fdf4" : "#fff"; }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setEventTypes(prev => checked ? prev.filter(v => v !== o.value) : [...prev, o.value])}
                        style={{ accentColor: "#16a34a" }}
                      />
                      {cfg && <cfg.Icon style={{ fontSize: 11, color: cfg.color }} />}
                      <span style={{ color: checked ? "#047857" : "#374151", fontWeight: checked ? 600 : 400 }}>{o.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Date range dropdown */}
          <div style={{ position: "relative" }} ref={dateDropdownRef}>
            <button
              onClick={() => setDateDropdownOpen(v => !v)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 14px", borderRadius: 8,
                border: "1px solid #d1d5db", background: "#fff",
                fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer",
              }}
            >
              <FaCalendarAlt size={11} color="#6b7280" />
              {dateLabel}
              <FaChevronDown size={9} color="#9ca3af" />
            </button>
            {dateDropdownOpen && (
              <div style={{
                position: "absolute", top: "100%", right: 0, marginTop: 4,
                background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)", zIndex: 30, minWidth: 160,
                overflow: "hidden",
              }}>
                {DATE_RANGE_OPTIONS.map(o => (
                  <div
                    key={o.value}
                    onClick={() => { setDateRange(o.value); setDateDropdownOpen(false); }}
                    style={{
                      padding: "8px 14px", cursor: "pointer", fontSize: 13,
                      background: dateRange === o.value ? "#f0fdf4" : "#fff",
                      fontWeight: dateRange === o.value ? 600 : 400, color: "#374151",
                    }}
                    onMouseEnter={e => { if (dateRange !== o.value) e.currentTarget.style.background = "#f9fafb"; }}
                    onMouseLeave={e => { if (dateRange !== o.value) e.currentTarget.style.background = "#fff"; }}
                  >
                    {o.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map + Panel */}
      <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: "hidden" }}>
        {/* Map area */}
        <div style={{ flex: 1, position: "relative", minHeight: isMobile ? 300 : 0 }}>
          {loading ? (
            <div style={{ width: "100%", height: "100%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{
                  width: 36, height: 36, border: "3px solid #e5e7eb", borderTopColor: "#1a3f6b",
                  borderRadius: "50%", animation: "spin 0.7s linear infinite", margin: "0 auto 12px",
                }} />
                <div style={{ fontSize: 13, color: "#6b7280" }}>Loading map data...</div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            </div>
          ) : (
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY} version="beta">
              <Map
                defaultCenter={center}
                defaultZoom={displayAddresses.length === 1 ? 15 : 13}
                mapId={MAP_ID}
                gestureHandling="greedy"
                disableDefaultUI={false}
                zoomControl={true}
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={false}
                style={{ width: "100%", height: "100%" }}
              >
                <ClusteredMarkers
                  addresses={displayAddresses}
                  onMarkerClick={handleMarkerClick}
                  activeMarkerId={activeMarkerId}
                />
              </Map>
            </APIProvider>
          )}

          {showDemo && (
            <DemoBanner
              isMobile={isMobile}
              title="Here's how Map Prospector looks with activity"
              subtitle="Sample data — addresses your contacts engage with on your website appear here automatically. Click a pin to see their activity."
            />
          )}
        </div>

        {/* Side panel / Bottom panel on mobile */}
        {activeAddress && (
          <div
            ref={panelRef}
            style={{
              width: isMobile ? "100%" : 360,
              maxHeight: isMobile ? "45vh" : "100%",
              overflowY: "auto",
              background: "#fff",
              borderLeft: isMobile ? "none" : "1px solid #e5e7eb",
              borderTop: isMobile ? "1px solid #e5e7eb" : "none",
              flexShrink: 0,
            }}
          >
            {/* Address header */}
            <div style={{
              padding: "16px 16px 12px", borderBottom: "1px solid #f0f0f0",
              display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", lineHeight: 1.3 }}>
                  {activeAddress.address || "Unknown address"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {activeAddress.events.length} event{activeAddress.events.length !== 1 ? "s" : ""}
                </div>
              </div>
              <button
                onClick={() => setActiveMarkerId(null)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: 4, color: "#9ca3af", flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>

            {/* Event timeline */}
            <div style={{ padding: "8px 16px 16px" }}>
              {activeAddress.events.map((evt, i) => {
                const cfg = EVENT_CONFIG[evt.event_type] || { label: evt.event_type, Icon: FaBell, color: "#6b7280" };
                const IconComp = cfg.Icon;
                const campaignLabel = evt.tag ? (CAMPAIGN_LABELS[evt.tag] || CAMPAIGN_LABELS[evt.tag?.toLowerCase()?.replace(/\s+/g, "_")] || evt.tag) : null;

                return (
                  <div key={i} style={{ display: "flex", gap: 10, marginBottom: 0 }}>
                    {/* Timeline line */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 24, flexShrink: 0 }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: `${cfg.color}15`, display: "flex",
                        alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <IconComp size={10} color={cfg.color} />
                      </div>
                      {i < activeAddress.events.length - 1 && (
                        <div style={{ width: 1, flex: 1, background: "#e5e7eb", minHeight: 16 }} />
                      )}
                    </div>
                    {/* Event detail */}
                    <div style={{ flex: 1, paddingBottom: 14, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 1 }}>
                        {evt.email}
                      </div>
                      {evt.phone && (
                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>
                          {evt.phone}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: cfg.color, fontWeight: 500, marginBottom: 2 }}>
                        {cfg.label}
                        {campaignLabel && (
                          <span style={{ color: "#9ca3af", fontWeight: 400 }}> · {campaignLabel}</span>
                        )}
                      </div>
                      {evt.subject && (
                        <div style={{
                          fontSize: 11, color: "#6b7280", marginBottom: 2,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {evt.subject}
                        </div>
                      )}
                      {evt.clicked_url && (
                        <a
                          href={evt.clicked_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={evt.clicked_url}
                          style={{
                            fontSize: 11, color: "#4338ca", textDecoration: "none",
                            display: "block", marginBottom: 2,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}
                        >
                          {evt.clicked_url}
                        </a>
                      )}
                      {evt.event_type === "Click" && evt.country && (
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }} title="Click origin country (Postmark Geo)">
                          🌍 {evt.country}
                        </div>
                      )}
                      {!evt.is_platform && evt.message_id && (
                        <a
                          href={`/email-details/${encodeURIComponent(evt.message_id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 11, color: "#0369a1", textDecoration: "none",
                            display: "inline-block", marginBottom: 2,
                          }}
                        >
                          View email →
                        </a>
                      )}
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        {formatTimestamp(evt.created_at)} {formatTime(evt.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
